import { defineModule } from '@nexus/core';
import {
  inspectionCreated,
  inspectionDeleted,
  inspectionStatusChanged,
  inspectionUpdated,
} from './events';

/** Placeholders of the inspection report in one group, e.g. `object.name`. */
function placeholders(group: string, names: readonly string[]) {
  return names.map((name) => ({
    key: `${group}.${name}`,
    label: `fire-safety.documents.placeholders.${group}.${name}`,
    group: `fire-safety.documents.groups.${group}`,
  }));
}

/** Contract between the module and the kernel (docs/architecture/modules.md). */
export const manifest = defineModule({
  id: 'fire-safety',
  name: 'fire-safety.name',
  description: 'fire-safety.description',
  icon: 'flame',
  theme: { accent: 'orange' },
  // Objects and projects of an inspection come from projects; the customer in the report is an
  // organisation of crm; norms for the legal basis of findings and their AI drafts from legal.
  dependsOn: ['projects', 'crm', 'legal'],
  permissions: [
    // Scope "own": inspections the person inspects or created (plan 7.1).
    { key: 'fire-safety.inspection.read', scopes: ['own', 'all'] },
    { key: 'fire-safety.inspection.write', scopes: ['own', 'all'] },
    { key: 'fire-safety.inspection.delete', privileged: true },
    { key: 'fire-safety.inspection.comment' },
    { key: 'fire-safety.inspection.export' },
    { key: 'fire-safety.settings.manage', privileged: true },
  ],
  defaultRoles: [
    {
      key: 'inspector',
      name: 'fire-safety.roles.inspector',
      grants: [
        { permission: 'fire-safety.inspection.read', scope: 'all' },
        { permission: 'fire-safety.inspection.write', scope: 'own' },
        { permission: 'fire-safety.inspection.comment', scope: null },
        { permission: 'fire-safety.inspection.export', scope: null },
      ],
    },
    {
      key: 'manager',
      name: 'fire-safety.roles.manager',
      grants: [
        // Unscoped permissions of the wildcard ignore the scope.
        { permission: 'fire-safety.inspection.*', scope: 'all' },
        { permission: 'fire-safety.settings.manage', scope: null },
      ],
    },
  ],
  navigation: [
    {
      label: 'fire-safety.nav.inspections',
      path: '/fire-safety/inspections',
      icon: 'clipboard-check',
      permission: 'fire-safety.inspection.read',
    },
  ],
  search: [{ entity: 'fire-safety.inspection', permission: 'fire-safety.inspection.read' }],
  // Reports of others handed over on site, e.g. test certificates, and photos of documents.
  files: [
    {
      entity: 'fire-safety.inspection',
      read: 'fire-safety.inspection.read',
      write: 'fire-safety.inspection.write',
      kinds: ['pdf', 'image'],
      maxBytes: 25_000_000,
    },
  ],
  activity: [
    {
      entity: 'fire-safety.inspection',
      read: 'fire-safety.inspection.read',
      comment: 'fire-safety.inspection.comment',
      path: '/fire-safety/inspections/$id',
    },
  ],
  views: [
    {
      list: 'fire-safety.inspections',
      read: 'fire-safety.inspection.read',
      share: 'fire-safety.inspection.write',
    },
  ],
  // Defects found on site, with photos and positions on the plans of object and project.
  findings: [
    {
      entity: 'fire-safety.inspection',
      read: 'fire-safety.inspection.read',
      write: 'fire-safety.inspection.write',
    },
  ],
  // The report the inspector sends after the inspection; companies design its templates.
  // `customer.*` is the client of the project, otherwise the operator, otherwise the owner of the
  // object (plan 7.5).
  documents: [
    {
      key: 'fire-safety.inspection-report',
      entity: 'fire-safety.inspection',
      label: 'fire-safety.documents.inspectionReport',
      permission: 'fire-safety.inspection.read',
      placeholders: [
        ...placeholders('object', [
          'name',
          'number',
          'customerNumber',
          'address',
          'street',
          'postcode',
          'city',
          'state',
          'usage',
          'buildingClass',
        ]),
        ...placeholders('customer', [
          'name',
          'number',
          'address',
          'phone',
          'email',
          'website',
          'contact',
        ]),
        ...placeholders('inspection', [
          'title',
          'date',
          'inspectors',
          'participants',
          'attendees',
          'status',
          'remarks',
        ]),
        ...placeholders('findings', ['count', 'open']),
      ],
      sources: [
        { key: 'attendees', kind: 'table', label: 'fire-safety.documents.sources.attendees' },
        { key: 'remarks', kind: 'text', label: 'fire-safety.documents.sources.remarks' },
      ],
    },
  ],
  // General information on the object, asked at every inspection. Defaults of the questions may
  // contain placeholders of the report, e.g. {{object.usage}}.
  questionnaires: [
    {
      key: 'fire-safety.general',
      entity: 'fire-safety.inspection',
      label: 'fire-safety.questionnaires.general',
      document: 'fire-safety.inspection-report',
    },
  ],
  // Only prepared inspections go to the device: one prepared itself, or those of a prepared object
  // or project (docs/architecture/offline.md, Vorbereiten).
  offline: [
    {
      entity: 'fire-safety.inspection',
      label: 'fire-safety.offline.inspections',
      mode: 'read-write',
      scope: 'prepared',
      conflict: 'field-merge',
      maxAge: 'P30D',
      permissions: { read: 'fire-safety.inspection.read', write: 'fire-safety.inspection.write' },
      pins: [
        { entity: 'projects.object', field: 'objectId' },
        { entity: 'projects.project', field: 'projectId' },
        // An inspection prepared itself, with the plans of its object and project.
        { entity: 'fire-safety.inspection', field: 'id' },
      ],
    },
  ],
  // Inspections belong to the object: on the object page first, on the project page after
  // SiGeKo, whose construction sites are projects.
  recordSections: [
    {
      key: 'fire-safety.object-inspections',
      target: 'projects.object',
      title: 'fire-safety.sections.inspections',
      permission: 'fire-safety.inspection.read',
      order: 10,
    },
    {
      key: 'fire-safety.project-inspections',
      target: 'projects.project',
      title: 'fire-safety.sections.inspections',
      permission: 'fire-safety.inspection.read',
      order: 20,
    },
  ],
  ai: [
    {
      // Drafts a finding from notes taken on site. Notes describe the building, not people; email
      // addresses, phone numbers and IBANs are replaced anyway.
      key: 'fire-safety.finding-draft',
      label: 'fire-safety.ai.findingDraft',
      data: 'none',
      entities: ['fire-safety.inspection'],
      permission: 'fire-safety.inspection.write',
      // One finding from bullet points with a fixed answer shape.
      model: 'small',
    },
  ],
  events: {
    publishes: [
      inspectionCreated.type,
      inspectionUpdated.type,
      inspectionStatusChanged.type,
      inspectionDeleted.type,
    ],
  },
  dataProcessing: [
    {
      entity: 'fire-safety.inspection',
      purpose:
        'Nachweis von Brandschutzbegehungen gegenüber Auftraggebern und Behörden: Begeher, Anwesende und Teilnehmer, Bemerkungen, Allgemeine Angaben, Feststellungen mit Fotos und Verantwortlichen und angehängte Nachweise; KI-Mangelerfassung aus Stichpunkten',
      legalBasis: 'Art. 6 Abs. 1 lit. b und f DSGVO',
      // Inspectors and the creator are account IDs. Attendees and participants are names and
      // functions of third parties; remarks, findings and photos can name or show people. Photos
      // keep the GPS position where they were taken.
      categories: ['account-id', 'name', 'job-title', 'free-text', 'photo', 'location'],
      // Proposal: ten years after the report went out (liability for the inspection), to be
      // confirmed with the DPO.
      retention: { period: 'P10Y', after: 'completed' },
      erase: 'anonymize',
      // Notes for the AI draft of a finding go to the AI provider the company picked.
      processors: ['ai-provider'],
    },
  ],
});
